import { useEffect } from 'react'
import { APP_DISPLAY_NAME, APP_SUPPORT_EMAIL } from '../config/app'
import { isCapacitorNative, isLineApp } from '../utils/platform'
import { getCurrentLocale, t, tx } from '../i18n/translations'

export type LegalType = 'privacy' | 'terms'

interface Props {
  open: boolean
  type: LegalType
  onClose: () => void
}

/**
 * プライバシーポリシー / 利用規約の表示モーダル。
 *
 * Sweeper の LegalModal.tsx を参考に、アプリ名だけナワバリ用に差し替えた
 * 完全文面を直埋めする。プラットフォーム（Capacitor / LINE / Web）ごとに
 * 本文内の記述を分岐して正確に反映する。
 */
const APP_NAME_JA = APP_DISPLAY_NAME
const COMPANY_JA = '株式会社ヤマネコワークス'
const COMPANY_EN = 'Yamaneko Works, Inc.'
const CONTACT_EMAIL = APP_SUPPORT_EMAIL
const LAST_UPDATED_JA = '2026年9月12日'
const LAST_UPDATED_EN = 'September 12, 2026'

export function LegalModal({ open, type, onClose }: Props) {
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = prev
    }
  }, [open])

  if (!open) return null

  const isNative = isCapacitorNative()
  const isLine = isLineApp()
  const locale = getCurrentLocale()
  const useJapanese = locale === 'ja'

  return (
    <div
      className="legal-modal__overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="legal-modal-title"
      onClick={onClose}
    >
      <div
        className="legal-modal__dialog"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="legal-modal__header">
          <h2 id="legal-modal-title" className="legal-modal__title">
            {type === 'privacy' ? t('privacyTitle') : t('termsTitle')}
          </h2>
          <button
            type="button"
            className="legal-modal__close"
            aria-label={t('closeAria')}
            onClick={onClose}
          >
            ×
          </button>
        </header>

        <div className="legal-modal__body" style={{ touchAction: 'pan-y' }}>
          <p className="legal-section__paragraph" style={{ opacity: 0.7 }}>
            {tx('legalLastUpdated', { date: useJapanese ? LAST_UPDATED_JA : LAST_UPDATED_EN })}
          </p>
          {useJapanese && type === 'terms' ? (
            <TermsJa isNative={isNative} isLine={isLine} />
          ) : useJapanese ? (
            <PrivacyJa isNative={isNative} isLine={isLine} />
          ) : type === 'terms' ? (
            <TermsEn isNative={isNative} isLine={isLine} />
          ) : (
            <PrivacyEn isNative={isNative} isLine={isLine} />
          )}
        </div>

        <footer className="legal-modal__footer">
          <button
            type="button"
            className="btn btn--primary"
            onClick={onClose}
          >
            {t('close')}
          </button>
        </footer>
      </div>
    </div>
  )
}

function TermsEn({ isNative, isLine }: { isNative: boolean; isLine: boolean }) {
  const platformDesc = isNative
    ? 'mobile game'
    : isLine
      ? 'game provided on the LINE Mini App platform'
      : 'web game'

  return (
    <>
      <p className="legal-section__paragraph">
        These Terms of Use define the conditions for using "{APP_NAME_JA}" (the "Service"), a {platformDesc} provided by {COMPANY_EN}.
      </p>
      <section className="legal-section">
        <h3 className="legal-section__heading">1. Agreement</h3>
        <p className="legal-section__paragraph">By using the Service, you agree to these Terms and our Privacy Policy. If you do not agree, stop using the Service.</p>
      </section>
      <section className="legal-section">
        <h3 className="legal-section__heading">2. License</h3>
        <p className="legal-section__paragraph">We grant you a limited, non-exclusive, non-transferable, revocable license to use the Service for personal, non-commercial purposes.</p>
      </section>
      <section className="legal-section">
        <h3 className="legal-section__heading">3. Prohibited Conduct</h3>
        <p className="legal-section__paragraph">You may not cheat, use bots or unauthorized tools, attack servers, exploit bugs, harass other users, reverse engineer the Service, or use inappropriate names or content.</p>
      </section>
      <section className="legal-section">
        <h3 className="legal-section__heading">4. Rankings and Scores</h3>
        <p className="legal-section__paragraph">We may collect and display score data as rankings. Scores obtained through improper means may be removed without notice.</p>
      </section>
      {isNative && (
        <section className="legal-section">
          <h3 className="legal-section__heading">5. Third-party Services</h3>
          <p className="legal-section__paragraph">The Service uses YamanekoGames Hub, Google Cloud, Apple Sign-In, Google Sign-In, and Google AdMob. Data may be processed according to the terms and privacy policies of those services.</p>
        </section>
      )}
      <section className="legal-section">
        <h3 className="legal-section__heading">{isNative ? '6' : '5'}. Disclaimer</h3>
        <p className="legal-section__paragraph">The Service is provided "as is". We do not guarantee uninterrupted or error-free operation and are not liable for damages except where required by applicable law.</p>
      </section>
      <section className="legal-section">
        <h3 className="legal-section__heading">{isNative ? '7' : '6'}. Changes and Termination</h3>
        <p className="legal-section__paragraph">We may change, suspend, or terminate the Service without prior notice.</p>
      </section>
      <section className="legal-section">
        <h3 className="legal-section__heading">{isNative ? '8' : '7'}. Governing Law</h3>
        <p className="legal-section__paragraph">These Terms are governed by Japanese law. The Tokyo District Court has exclusive jurisdiction for disputes related to the Service.</p>
      </section>
      <section className="legal-section">
        <h3 className="legal-section__heading">Contact</h3>
        <p className="legal-section__paragraph">{COMPANY_EN}{'\n'}{CONTACT_EMAIL}</p>
      </section>
    </>
  )
}

function PrivacyEn({ isNative, isLine }: { isNative: boolean; isLine: boolean }) {
  const appDesc = isNative ? 'mobile app' : isLine ? 'LINE Mini App' : 'web app'

  return (
    <>
      <p className="legal-section__paragraph">
        {COMPANY_EN} defines this Privacy Policy for the handling of personal information in the {appDesc} "{APP_NAME_JA}".
      </p>
      <section className="legal-section">
        <h3 className="legal-section__heading">1. Information We Collect</h3>
        <p className="legal-section__paragraph">
          We may collect account identifiers, display names, avatar or profile images, game scores, ranking data, play history, IP address, device type, OS version, and other technical information.
          {isLine && '\nWhen used through LINE, we may receive LINE User ID, public profile information, and language settings.'}
          {isNative && '\nWhen account linking is used, we may receive Apple or Google account identifiers, email address, display name, and authentication tokens. We may also use advertising identifiers, ad delivery data, and consent status for ad delivery and measurement.'}
        </p>
      </section>
      <section className="legal-section">
        <h3 className="legal-section__heading">2. Purposes of Use</h3>
        <p className="legal-section__paragraph">We use collected information to provide and operate the app, maintain rankings, save game progress, provide support, analyze usage, improve the Service, prevent fraud, and ensure security.{isNative && '\nWe also use information for ad delivery, ad measurement, and ad consent management.'}</p>
      </section>
      <section className="legal-section">
        <h3 className="legal-section__heading">3. Third-party Services</h3>
        <p className="legal-section__paragraph">The app uses YamanekoGames Hub and Google Cloud for authentication, profile management, game data, and rankings. Data may be stored outside Japan, primarily in the United States.</p>
        {isLine && <p className="legal-section__paragraph">The LINE platform may be used for authentication, profile retrieval, and sharing.</p>}
        {isNative && <p className="legal-section__paragraph">Apple Sign-In, Google Sign-In, Google AdMob, and Google User Messaging Platform may process data according to their own terms and privacy policies. You may be shown an ad consent form depending on your region, and ad privacy choices may be available from Settings &gt; Info.</p>}
        <p className="legal-section__paragraph">Google Analytics (Google LLC) is used to measure how the app is used. See "4. Usage Analytics" for details.</p>
      </section>
      <section className="legal-section">
        <h3 className="legal-section__heading">4. Usage Analytics</h3>
        <p className="legal-section__paragraph">To improve this app, we may use Google Analytics, provided by Google LLC, to measure how the app is used. The information sent for measurement consists of app launches and screens reached, the kind of action taken (such as starting or finishing a game, submitting a score, or watching an ad), whether the launch came through an ad, the type of device or browser, and an approximate region. For measurement, Google Analytics stores an identifier that distinguishes the browser (device) in cookies or similar storage and sends it to Google together with the action information. This identifier does not identify you personally, and we do not match it against your LINE user ID or other identifiers. Your name, LINE user ID, and profile information are never sent. The handling of the collected information is governed by Google's Privacy Policy (https://policies.google.com/privacy).</p>
      </section>
      <section className="legal-section">
        <h3 className="legal-section__heading">5. Retention and Deletion</h3>
        <p className="legal-section__paragraph">User data is retained as long as needed to provide the app, comply with law, prevent fraud, and maintain security. We use encryption in transit, access controls, separated authentication data, and operational logs to protect user data. In the app version, deletion can be started from Settings &gt; Account &gt; Delete Account. If you cannot use the app, contact us at the address below.</p>
      </section>
      <section className="legal-section">
        <h3 className="legal-section__heading">6. GDPR and CCPA Rights</h3>
        <p className="legal-section__paragraph">Users in the EEA and California may have rights including access, correction, deletion, and data portability. Contact us to exercise these rights.</p>
      </section>
      <section className="legal-section">
        <h3 className="legal-section__heading">7. Children</h3>
        <p className="legal-section__paragraph">The app is not primarily directed to children under 13, and we do not knowingly collect personal information from children under 13.</p>
      </section>
      <section className="legal-section">
        <h3 className="legal-section__heading">8. Contact</h3>
        <p className="legal-section__paragraph">{COMPANY_EN}{'\n'}{CONTACT_EMAIL}</p>
      </section>
    </>
  )
}

/* ============================================================
   利用規約
   ============================================================ */
function TermsJa({ isNative, isLine }: { isNative: boolean; isLine: boolean }) {
  const platformDesc = isNative
    ? 'モバイルアプリケーションとして提供するゲーム'
    : isLine
      ? 'LINE Mini Appプラットフォーム上で提供するゲーム'
      : 'Webアプリケーションとして提供するゲーム'

  let sectionIndex = 5

  return (
    <>
      <p className="legal-section__paragraph">
        この利用規約（以下「本規約」といいます）は、{COMPANY_JA}（以下「当社」といいます）が
        {platformDesc}「{APP_NAME_JA}」（以下「本サービス」といいます）の利用条件を定めるものです。
      </p>

      <section className="legal-section">
        <h3 className="legal-section__heading">1. 規約への同意</h3>
        <p className="legal-section__paragraph">
          ユーザーは、本サービスを利用することにより、本規約および当社のプライバシーポリシーに同意したものとみなされます。同意いただけない場合は、本サービスの利用を中止してください。
        </p>
      </section>

      <section className="legal-section">
        <h3 className="legal-section__heading">2. ライセンスの付与</h3>
        <p className="legal-section__paragraph">
          当社はユーザーに対し、個人的かつ非商用目的でのみ本サービスを利用できる、非独占的、譲渡不能、取消可能な限定的ライセンスを付与します。
        </p>
      </section>

      <section className="legal-section">
        <h3 className="legal-section__heading">3. 禁止事項</h3>
        <p className="legal-section__paragraph">
          ユーザーは、本サービスの利用にあたり、以下の行為を行ってはなりません。
        </p>
        <p className="legal-section__paragraph">
          ・不正行為（チート）: 自動化ソフトウェア（ボット）、Mod、ハッキングツール、その他本サービスの仕組みを不正に操作するツールの使用。{'\n'}
          ・運営妨害: サーバーへの攻撃、バグの意図的な悪用、他のユーザーへの迷惑行為。{'\n'}
          ・リバースエンジニアリング: 本サービスのソースコードの解析、改変、二次利用。{'\n'}
          ・不適切なコンテンツ: 公序良俗に反する名前やコンテンツの使用（ランキング表示名などを含む）。
        </p>
      </section>

      <section className="legal-section">
        <h3 className="legal-section__heading">4. ランキングとスコア</h3>
        <p className="legal-section__paragraph">
          当社は、ユーザーのスコアデータを収集し、ランキングとして表示する権利を有します。不正な手段により獲得されたと当社が判断したスコアは、予告なく削除される場合があります。
        </p>
      </section>

      {isNative && (
        <section className="legal-section">
          <h3 className="legal-section__heading">5. サードパーティサービス</h3>
          <p className="legal-section__paragraph">
            本サービスは、ゲームデータの管理およびランキング機能の提供のために、Google Cloud上で稼働するサーバーおよびYamanekoGames SDK（当社が提供する専用ソフトウェア開発キット）を使用しています。アカウント連携・復旧のために、Apple Sign-In（Apple Inc.提供）およびGoogle Sign-In（Google LLC提供）を使用しています。また、広告配信のためにGoogle AdMobを使用しています。これらのサービスの利用に伴い、ユーザーのデータが当該サービスの利用規約に従い処理されます。
          </p>
        </section>
      )}

      <section className="legal-section">
        <h3 className="legal-section__heading">{isNative ? sectionIndex++ : 5}. 免責事項</h3>
        <p className="legal-section__paragraph">
          ・本サービスは「現状有姿」で提供されます。{'\n'}
          ・当社は、本サービスにバグや不具合がないこと、中断なく動作することを保証しません。{'\n'}
          ・当社は、本サービスの利用に起因してユーザーに生じたあらゆる損害（データの消失、端末の故障など）について、適用法で認められる範囲において一切の責任を負いません。
        </p>
      </section>

      <section className="legal-section">
        <h3 className="legal-section__heading">{isNative ? sectionIndex++ : 6}. サービスの変更・終了</h3>
        <p className="legal-section__paragraph">
          当社は、ユーザーへの事前の通知なく、本サービスの内容を変更、一時停止、または終了する権利を有します。
        </p>
      </section>

      <section className="legal-section">
        <h3 className="legal-section__heading">{isNative ? sectionIndex++ : 7}. 準拠法および管轄裁判所</h3>
        <p className="legal-section__paragraph">
          本規約は日本法に準拠し、解釈されるものとします。本サービスに関わる一切の紛争については、東京地方裁判所を第一審の専属的合意管轄裁判所とします。
        </p>
      </section>

      <section className="legal-section">
        <h3 className="legal-section__heading">お問い合わせ</h3>
        <p className="legal-section__paragraph">
          本規約に関するご質問は、下記連絡先までお問い合わせください。
        </p>
        <p className="legal-section__paragraph">
          {COMPANY_JA}{'\n'}
          {CONTACT_EMAIL}
        </p>
      </section>
    </>
  )
}

/* ============================================================
   プライバシーポリシー
   ============================================================ */
function PrivacyJa({ isNative, isLine }: { isNative: boolean; isLine: boolean }) {
  const appDesc = isNative
    ? `モバイルアプリ「${APP_NAME_JA}」`
    : isLine
      ? `LINE Mini App「${APP_NAME_JA}」`
      : `Webアプリ「${APP_NAME_JA}」`

  return (
    <>
      <p className="legal-section__paragraph">
        {COMPANY_JA}（以下「当社」といいます）は、{appDesc}（以下「本アプリ」といいます）におけるユーザーの個人情報の取り扱いについて、以下の通りプライバシーポリシー（以下「本ポリシー」といいます）を定めます。
      </p>

      <section className="legal-section">
        <h3 className="legal-section__heading">1. 収集する情報</h3>
        <p className="legal-section__paragraph">
          本アプリでは、以下の情報を収集・利用する場合があります。
        </p>

        {isLine && (
          <>
            <p className="legal-section__paragraph">
              【LINEから提供される情報】{'\n'}
              ・ユーザーID（LINE User ID）{'\n'}
              ・公開プロフィール情報（表示名、プロフィール画像）{'\n'}
              ・言語設定
            </p>
          </>
        )}

        {isNative && (
          <p className="legal-section__paragraph">
            【アカウント情報】{'\n'}
            ・ゲスト識別子（自動生成される匿名ID）{'\n'}
            ・Apple IDアカウント情報（Sign in with Appleを利用する場合：ユーザー識別子、メールアドレス ※Appleの「メールを非公開」機能により転送用アドレスが提供される場合があります）{'\n'}
            ・Googleアカウント情報（アカウント連携を利用する場合：表示名、メールアドレス）{'\n'}
            ・ユーザーが設定した表示名およびアバター画像
          </p>
        )}

        {!isLine && !isNative && (
          <p className="legal-section__paragraph">
            【アカウント情報】{'\n'}
            ・ゲスト識別子（自動生成される匿名ID）{'\n'}
            ・ユーザーが設定した表示名およびアバター画像
          </p>
        )}

        <p className="legal-section__paragraph">
          【ゲームプレイデータ】{'\n'}
          ・ゲームのスコア、ランキングデータ、プレイ履歴
        </p>

        <p className="legal-section__paragraph">
          【技術情報】{'\n'}
	          ・IPアドレス、デバイスの種類、OSのバージョンなどの端末情報
	          {isNative && `\n・広告識別子（Google Advertising ID / IDFA）、広告配信・広告効果測定に関する情報、広告同意状態`}
        </p>
      </section>

      <section className="legal-section">
        <h3 className="legal-section__heading">2. 情報の利用目的</h3>
        <p className="legal-section__paragraph">
          収集した情報は、以下の目的のために利用されます。{'\n'}
          ・本アプリの提供および運営（ランキング機能の維持、ゲーム進行状況の保存など）{'\n'}
          ・ユーザーサポートの提供{'\n'}
          ・利用状況の分析およびサービスの改善{'\n'}
	          ・不正行為の防止およびセキュリティの確保
	          {isNative && `\n・広告の配信、広告効果の測定、広告同意状態の管理`}
        </p>
      </section>

      <section className="legal-section">
        <h3 className="legal-section__heading">3. 第三者への提供および外部サービスの利用</h3>
        <p className="legal-section__paragraph">
          当社は、本アプリの提供にあたり、以下の第三者サービスを利用しており、必要な範囲でユーザーデータが送信・保存されます。
        </p>

        <p className="legal-section__paragraph">
          【YamanekoGames Hub / Google Cloud (Google LLC)】{'\n'}
          ・用途: ユーザー認証、ゲームデータおよびランキングの保存・管理、プロフィール管理{'\n'}
          ・技術基盤: 本アプリは当社が提供するYamanekoGames Hub APIと通信します。Webブラウザ版のGoogleサインインではFirebase Authenticationを使用する場合があります。{'\n'}
          ・データの所在: 本アプリのデータは、主に米国（US-Central）のサーバーに保存されます。ユーザーは、自身のデータが日本国外（米国）に移転・保存されることに同意するものとします。
        </p>

        {isLine && (
          <p className="legal-section__paragraph">
            【LINEプラットフォーム (LINEヤフー株式会社)】{'\n'}
            ・用途: ユーザー認証、プロフィール情報の取得、シェア機能の提供
          </p>
        )}

        {isNative && (
          <>
            <p className="legal-section__paragraph">
              【Sign in with Apple (Apple Inc.)】{'\n'}
              ・用途: アカウント連携および復旧（iOS版）{'\n'}
              ・送信データ: Apple認証トークン（IDトークン）{'\n'}
              ・取得データ: Appleユーザー識別子、メールアドレス（ユーザーが許可した場合。Appleの「メールを非公開」機能により、ランダムな転送用メールアドレスが提供される場合があります）
            </p>
            <p className="legal-section__paragraph">
              【Google Sign-In (Google LLC)】{'\n'}
              ・用途: アカウント連携および復旧{'\n'}
              ・送信データ: Google認証トークン（IDトークン）{'\n'}
              ・取得データ: Googleアカウントの表示名、メールアドレス、プロフィール画像（ユーザーが許可した場合）
            </p>
          </>
        )}

        {isNative ? (
          <p className="legal-section__paragraph">
            【広告配信について (Google LLC)】{'\n'}
	            本アプリでは、Google AdMob（Google LLC提供）および Google User Messaging Platform を利用して広告を配信しています。AdMobはユーザーの広告識別子、デバイス情報、広告配信に関する情報を取得し、同意状態に応じてパーソナライズド広告または非パーソナライズド広告の配信、広告効果の測定に利用する場合があります。広告プライバシー設定は「設定 {'>'} 情報」から変更できる場合があります。
          </p>
        ) : null}

        <p className="legal-section__paragraph">
          【Google アナリティクス (Google LLC)】{'\n'}
          ・用途: 本アプリの利用状況の計測および改善（詳細は「4. 利用状況の計測について」）
        </p>
      </section>

      <section className="legal-section">
        <h3 className="legal-section__heading">4. 利用状況の計測について</h3>
        <p className="legal-section__paragraph">
          本アプリの改善のため、Google LLC が提供する Google アナリティクスを利用して、アプリの利用状況を計測することがあります。計測のために送信されるのは、アプリの起動や画面の到達状況、ゲームの開始・終了・スコアの送信・広告の視聴といった操作の種類、広告を経由した起動かどうか、端末やブラウザの種類、おおまかな地域といった情報です。計測にあたり、Google アナリティクスはブラウザ（端末）を区別するための識別子を Cookie などに保存し、各操作の情報とあわせて Google に送信します。この識別子はお客様個人を特定するものではなく、当社が LINE のユーザー ID などと突き合わせることもありません。お名前、LINE のユーザー ID、プロフィール情報は送信しません。取得された情報の取り扱いは Google のプライバシーポリシー（https://policies.google.com/privacy）に従います。
        </p>
      </section>

      <section className="legal-section">
        <h3 className="legal-section__heading">5. データの保存と削除</h3>
        <p className="legal-section__paragraph">
	          ・保存期間: ユーザーデータは、本アプリの提供、法令遵守、不正利用防止、セキュリティ確保に必要な期間保存されます。{'\n'}
	          ・安全管理: 通信の暗号化、アクセス権限の制限、認証情報の分離管理、操作ログの確認など、漏えい、滅失、毀損を防ぐために必要な措置を講じます。{'\n'}
	          ・削除請求: ユーザーは、当社に対し自身の個人データの削除を請求することができます。アプリ版では「設定 {'>'} アカウント {'>'} アカウント削除（退会）」から削除を開始できます。アプリを利用できない場合は、下記連絡先へお問い合わせください。なお、データ削除後は、ランキングからの除外やゲームデータの初期化が行われます。法令遵守、不正利用防止、セキュリティ、課金・広告不正対策のために必要な記録は、必要最小限の範囲で保持する場合があります。
        </p>
      </section>

      <section className="legal-section">
        <h3 className="legal-section__heading">6. GDPRおよびCCPAに関する権利（海外ユーザー向け）</h3>
        <p className="legal-section__paragraph">
          EEA（欧州経済領域）およびカリフォルニア州のユーザーは、アクセス権、訂正権、削除権、データポータビリティ権などを有します。これらの権利を行使する場合は、当社までご連絡ください。
        </p>
      </section>

      <section className="legal-section">
        <h3 className="legal-section__heading">7. 子供のプライバシー</h3>
        <p className="legal-section__paragraph">
          本アプリは、13歳未満の児童を主な対象としていません。13歳未満の児童から意図的に個人情報を収集することはありません。
        </p>
      </section>

      <section className="legal-section">
        <h3 className="legal-section__heading">8. お問い合わせ</h3>
        <p className="legal-section__paragraph">
          本ポリシーに関するご質問は、下記連絡先までお問い合わせください。
        </p>
        <p className="legal-section__paragraph">
          {COMPANY_JA}{'\n'}
          {CONTACT_EMAIL}
        </p>
      </section>
    </>
  )
}
